import type {
	ScorerRequestColumn,
	ScreenerColumn,
	ScreenerState,
	ScreenerSubField,
} from "epsm-api-types/models";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import type { SingleValue } from "react-select";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useWidgetView } from "@/context/WidgetViewContext";
import { useEventSubscription } from "@/hooks/useEventSubscription";
import type { Option } from "@/interfaces/Option";
import { flattenSubfieldsToDropdownOptions } from "@/lib/utils/flattenSubfieldsToDropdownOptions";
import { EVENTS } from "@/shared/eventEmiters/consts";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import sprites from "/src/shared/assets/sprite.svg";
import { scorerPrefix } from "../default";
import { Dropdown } from "../ScreenerFilters/Filters/ui/Dropdown";
import { ScreenerColumnService } from "../services/ScreenerColumnService";
import type { ScorerColumn } from "../type";
import { addScoringColumns } from "./helpers/addScoringColumns/addScoringColumns";
import { getNestedName } from "./helpers/getNestedName";
import { updateDropdownWithSubfields } from "./helpers/updateDropdownWithSubfields";
import { weightSchema } from "./weightSchema";

export const getEqualWeight = (columns: ScorerColumn[]): number => {
	const totalColumns = columns.length;
	if (totalColumns === 0) return 0;
	return 100 / totalColumns;
};

type ScorerProps = {
	setResultsScorer: Dispatch<SetStateAction<ScorerRequestColumn[]>>;
	onStateChange?: OnStateChange;
	setShouldFetchScorer: Dispatch<SetStateAction<boolean>>;
	eventEmitter: EventEmitter;
	setScreenerState: Dispatch<SetStateAction<ScreenerState>>;
};

export const getColumnName = (column: ScreenerColumn) => {
	return column.name.startsWith(scorerPrefix)
		? column.name.replace(scorerPrefix, "")
		: column.name;
};

export const Scorer: React.FC<ScorerProps> = ({
	setResultsScorer,
	onStateChange,
	setShouldFetchScorer,
	eventEmitter,
	setScreenerState,
}) => {
	const { scorerColumns, setScorerColumns } = useWidgetView();
	const [isEqualWeight, setEqualWeight] = useState<boolean>(true);
	const [allSubFields, setAllSubFields] = useState<
		Record<string, ScreenerSubField[]>
	>({});

	useEffect(() => {
		ScreenerColumnService.create()
			.getData()
			.then((columns) => {
				const subfieldsMap: Record<string, ScreenerSubField[]> = {};
				if (columns?.data) {
					for (const column of columns.data) {
						if (column.subFields && column.subFields.length > 0) {
							subfieldsMap[column.name] = column.subFields;
						}
					}
					setAllSubFields(subfieldsMap);
				}
			});
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we don't want to run this effect on every scorerColumns changes
	useEffect(() => {
		if (isEqualWeight) {
			const equalWeight = getEqualWeight(scorerColumns);
			setScorerColumns((prev) =>
				prev.map((col) => ({ ...col, weight: equalWeight })),
			);
		}
	}, [isEqualWeight, scorerColumns.length]);

	useEventSubscription(eventEmitter, EVENTS.SCORER, (data: boolean) => {
		if (!data) {
			setScreenerState((prev) => ({
				...prev,
				fields: prev.fields?.filter(
					(column) => !column.startsWith(scorerPrefix),
				),
			}));
		}
	});

	const sortOptions = [
		{ value: "Higher is better", label: "Higher is better" },
		{ value: "Lower is better", label: "Lower is better" },
	];

	const totalWeights = Math.round(
		scorerColumns.reduce((acc, column) => acc + (column.weight || 0), 0),
	);

	return (
		<div className="min-w-40 w-full border-box rounded-sm">
			<div className="flex justify-between px-2.5 py-1">
				<h3 className="text-accent-foreground italic">Scorer</h3>
				<h3 className="px-1.75">Weight</h3>
			</div>

			<div className="flex flex-col max-h-base h-full px-2.5 justify-between">
				<div className="flex-col items-center gap-2 h-[144px] overflow-y-scroll">
					{scorerColumns.map((column) => {
						return (
							<div key={column.name} className="flex flex-col">
								<div className="flex items-center gap-3 mb-0.5">
									<Button
										className="h-max p-0 shadow-none group bg-transparent"
										onClick={() => {
											setScorerColumns((prev) =>
												prev.filter((col) => col.name !== column.name),
											);
										}}
									>
										<svg
											aria-label="close"
											role="img"
											fill="none"
											stroke="var(--light-gray)"
											className="w-3 h-3 group-hover:stroke-accent-foreground"
										>
											<use xlinkHref={`${sprites}#close`} />
										</svg>
									</Button>

									<div className="w-auto flex items-center">
										<Tooltip>
											<TooltipTrigger asChild>
												<div
													className="flex justify-between hover:shadow-none h-max 
											p-0.5 text-[var(--light-gray)] hover:text-accent-foreground w-fit"
												>
													<div className="flex-1">
														{column?.titleShort ?? column?.title}
													</div>
												</div>
											</TooltipTrigger>
											<TooltipContent side="right">
												<span>{column.title}</span>
											</TooltipContent>
										</Tooltip>
									</div>

									<div className="flex items-center gap-1.5 ml-auto">
										<Dropdown
											placeholder={sortOptions[0].label}
											options={sortOptions}
											value={
												column.order === 1 ? sortOptions[1] : sortOptions[0]
											}
											onChange={(value) => {
												setScorerColumns((prev) =>
													prev.map((col) =>
														col.name === column.name
															? {
																	...col,
																	order: (
																		(value as SingleValue<Option>)
																			?.value as string
																	).startsWith("Lower")
																		? 1
																		: -1,
																}
															: col,
													),
												);
											}}
											maxMenuHeight={80}
											isTypeList={false}
											className="w-[150px] mr-1.5 h-4.5"
										/>

										<Input
											className="text-center w-10 bg-background"
											value={Math.round(column.weight || 0)}
											onChange={(event) => {
												const rawValue = event.target.value;
												const result = weightSchema.safeParse(rawValue);

												if (!result.success) {
													return;
												}

												setScorerColumns((prev) =>
													prev.map((col) =>
														col.name === column.name
															? { ...col, weight: result.data }
															: col,
													),
												);
												setEqualWeight(false);
											}}
										/>
									</div>
								</div>
								{allSubFields[getColumnName(column)]?.length && (
									<Dropdown
										placeholder="Select subfield..."
										options={flattenSubfieldsToDropdownOptions(
											allSubFields[getColumnName(column)],
										)}
										value={
											column.subFields?.flatMap((subfield) => {
												return flattenSubfieldsToDropdownOptions([subfield]);
											}) || []
										}
										onChange={(value) => {
											updateDropdownWithSubfields(
												column,
												value as SingleValue<Option>,
												allSubFields,
												setScorerColumns,
											);
										}}
										maxMenuHeight={80}
										isTypeList={false}
										className="w-[130px] mr-1.5 ml-6 gap-[12px]"
									/>
								)}
							</div>
						);
					})}
				</div>

				<div>
					<div className="flex items-end gap-4 justify-between pl-1 pr-2 text-light-gray">
						<div className="flex gap-2 items-center">
							<label htmlFor="eq-weight" className="cursor-pointer font-normal">
								Eq. Weight
							</label>
							<Checkbox
								id="eq-weight"
								checked={isEqualWeight}
								onCheckedChange={(value) => {
									setEqualWeight(value as boolean);
								}}
								className="w-30px"
							/>
						</div>

						<div className="flex justify-end gap-3">
							<div className="flex justify-between gap-1">
								<span>Match:</span>
								<span>100</span>
							</div>
							<div className="flex justify-between gap-1">
								<span>Total:</span>
								<span>{totalWeights}</span>
							</div>
						</div>
					</div>

					<div className="flex justify-end mt-1">
						<Button
							variant={"outline"}
							className={`py-1.5 px-2.5 h-5 mr-0.5 ${totalWeights !== 100 ? "cursor-not-allowed disabled opacity-50" : ""}`}
							onClick={() => {
								const flattenedScroreColumns = scorerColumns.map((col) => {
									return {
										...col,
										name: getNestedName(col),
									};
								});

								setScreenerState((prev) => ({
									...prev,
									fields: addScoringColumns(
										flattenedScroreColumns,
										prev.fields || [],
									),
								}));
								const results = flattenedScroreColumns.map((col) => ({
									name: getColumnName(col),
									weight: col.weight || 0,
									order: col.order || -1,
								}));
								setResultsScorer(results);
								onStateChange?.({ scorerData: results });
								setShouldFetchScorer(true);
							}}
						>
							Add
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
